import { cn } from '@/utils/cn'

export type SkeletonVariant = 'line' | 'block' | 'circle' | 'table' | 'form' | 'card'

export interface SkeletonProps {
  readonly variant?: SkeletonVariant
  /** Rows for `table` / `form` / repeated `line`. */
  readonly rows?: number
  /** Columns for `table`. */
  readonly cols?: number
  readonly className?: string
  /** Announced to screen readers while content loads. */
  readonly label?: string
}

const base = 'skeleton-pulse rounded-sm bg-surface-muted'

/**
 * The ONE skeleton component. Every loading state in the app is a skeleton that
 * mirrors the final layout — never a bare spinner.
 */
export function Skeleton({
  variant = 'line',
  rows = 1,
  cols = 4,
  className,
  label,
}: SkeletonProps) {
  const wrapperProps = {
    role: 'status' as const,
    'aria-busy': true,
    'aria-live': 'polite' as const,
    'aria-label': label ?? 'Loading',
  }

  if (variant === 'table') {
    return (
      <div {...wrapperProps} className={cn('w-full overflow-hidden', className)}>
        <div className="flex gap-3 border-b border-border px-4 py-3">
          {Array.from({ length: cols }, (_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
            <div key={`h-${i}`} className={cn(base, 'h-3 flex-1')} />
          ))}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
            key={`r-${r}`}
            className="flex gap-3 border-b border-border px-4 py-4 last:border-b-0"
          >
            {Array.from({ length: cols }, (_, c) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
              <div key={`c-${c}`} className={cn(base, 'h-4 flex-1', c === 0 && 'max-w-[8rem]')} />
            ))}
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'form') {
    return (
      <div {...wrapperProps} className={cn('space-y-5', className)}>
        {Array.from({ length: rows }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
          <div key={i} className="space-y-2">
            <div className={cn(base, 'h-3 w-32')} />
            <div className={cn(base, 'h-10 w-full')} />
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'card') {
    return (
      <div {...wrapperProps} className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
        {Array.from({ length: rows }, (_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
            key={i}
            className="space-y-3 rounded-lg border border-border bg-surface p-4"
          >
            <div className={cn(base, 'h-4 w-1/2')} />
            <div className={cn(base, 'h-3 w-3/4')} />
            <div className={cn(base, 'h-3 w-2/3')} />
          </div>
        ))}
      </div>
    )
  }

  if (variant === 'circle') {
    return <div {...wrapperProps} className={cn(base, 'size-9 rounded-full', className)} />
  }

  if (variant === 'block') {
    return <div {...wrapperProps} className={cn(base, 'h-24 w-full', className)} />
  }

  return (
    <div {...wrapperProps} className={cn('space-y-2', className)}>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder rows; the index IS the identity
        <div key={i} className={cn(base, 'h-4', i === rows - 1 && rows > 1 ? 'w-2/3' : 'w-full')} />
      ))}
    </div>
  )
}
